import React, { useReducer, useMemo, useEffect } from 'react';
import styled from '@emotion/styled';
import { breakpoints, colors, css, respondFrom } from '@styles';
import Accordion from '@components/common/Accordion';
import BorderLine from '@components/common/BorderLine';
import ReactTooltip from 'react-tooltip';
import {
    Funding,
    ConfiguratorBasket,
    ConfiguratorDependent,
    BasketDiscountCoupon,
} from '@store/types';
import { ConfiguratorSettings } from '@content/types/configuratorSettings';
import TooltipIcon from '@components/common/TooltipIcon';
import get from 'lodash.get';
import * as utils from '@utils';

const StyledFunding = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    &:not(:last-child) {
        padding-bottom: 35px;
        border-bottom: 1px solid #848484;
        margin-bottom: 35px;
    }
    ${respondFrom(
        breakpoints.lg,
        css`
            padding-bottom: 0;
            border: 0;
           &:not(:last-child) {
                margin-bottom: 20px;
            }
        `
    )};
`;

const StyledFundings = styled.div`
    padding-bottom: 35px;
    border-bottom: 1px solid #848484;
    &:not(:last-child) {
        margin-top: 35px;
        margin-bottom: 35px;
    }
    ${respondFrom(
        breakpoints.lg,
        css`
            padding-bottom: 0;
            border: 0;
           &:not(:last-child) {
                margin-top: 20px;
                margin-bottom: 20px;
            }
        `
    )};
`;

const StyledDiscountCoupon = styled.div`
    padding-bottom: 35px;
    border-bottom: 1px solid #848484;
    &:not(:last-child) {
        margin-bottom: 35px;
    }
    ${respondFrom(
        breakpoints.lg,
        css`
            padding-bottom: 0;
            border: 0;
           &:not(:last-child) {
                margin-bottom: 20px;
            }
        `
    )};
`;

const StyledDiscountCouponWrapper = styled.div`
    display: flex;
    align-items: center;
`;

const StyledDiscountCouponError = styled.div`
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    color: ${colors.red.error};
    margin: 5px 0;
`;

const StyledReturnPayment = styled.div`
    display: flex;
    align-items: center;
    padding-bottom: 35px;
    border-bottom: 1px solid #848484;
    &:not(:last-child) {
        margin-bottom: 35px;
    }
    ${respondFrom(
        breakpoints.lg,
        css`
            padding-bottom: 0;
            border: 0;
           &:not(:last-child) {
                margin-bottom: 20px;
            }
        `
    )};
`;

const StyledWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledName = styled.div<StyledNameProps>`
    font-size: 13px;
    font-weight: ${({ isAdded }) => isAdded ? "bold" : "normal"};
    color: ${({ isAdded }) => isAdded ? colors.green.summary : colors.grayLight};
    display: flex;
    align-items: center;
`;

const StyledButton = styled.button<StyledButtonProps>`
    font-size: 12px;
    line-height: 1px;
    font-weight: 500;
    white-space: nowrap;
    color: ${({ isAdded }) => isAdded ? colors.black : colors.green.summary};
    border: 2px solid  ${({ isAdded }) => isAdded ? "#a7a7a7" : colors.green.summary};
    background-color: ${colors.white};
    margin-left: 15px;
    cursor: pointer;
    height: 40px;
    min-width: 102px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.3s, color 0.3s;
    &:hover {
        ${({ isAdded }) => !isAdded && css`
            background-color: ${colors.green.summary};
            color: ${colors.white};
        `};
    }
    ${({ disabled }) => disabled && css`
        opacity: 0.5;
        pointer-events: none;
    `};
    ${respondFrom(
        breakpoints.lg,
        css`
           height: 32px;
        `
    )};
`;

const StyledInput = styled.input<StyledInputProps>`
    width: 100%;
    max-width: 156px;
    height: 40px;
    outline: none;
    font-size: 13px;
    font-weight: 500;
    line-height: 1em;
    color: ${colors.black};
    margin-left: 15px;
    padding: 0 10px;
    &::placeholder {
        color: '#a7a7a7';
    }
    ${({ isError }) => isError && css`
        border: 2px solid ${colors.red.error};
    `};
    ${({ disabled }) => disabled && css`
        opacity: 0.5;
        pointer-events: none;
    `};
    ${respondFrom(
        breakpoints.lg,
        css`
           height: 32px;
        `
    )};
`;

interface Props extends ConfiguratorDependent {
    allFundings: Funding[];
    currentFundings: Funding[];
    basket: ConfiguratorBasket;
    pageSettings: ConfiguratorSettings;
    paymentType: string;
    onAfterRemoveDiscount: Function;
}

interface StyledNameProps {
    isAdded?: boolean;
}

interface StyledButtonProps {
    isAdded?: boolean;
    disabled?: boolean;
}

interface StyledInputProps {
    isError?: boolean;
    disabled?: boolean; 
}

const defaulDiscountCoupontState = {
    code: "",
    isError: false,
    isLoading: false
}

function discountCouponReducer(state, action) {
    switch (action.type) {
        case 'set_code': {
            return {
                ...state,
                code: action.payload
            }
        }
        case 'set_is_error': {
            return {
                ...state,
                isError: action.payload
            }
        }
        case 'set_is_loading': {
            return {
                ...state,
                isLoading: action.payload
            }
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`)
        }
    }
};

const ShoppingSummaryDiscounts = ({ allFundings, configuratorActions, currentFundings, basket, pageSettings, configurator, paymentType, onAfterRemoveDiscount }: Props) => {
    const [discountCoupon, discountCouponDispatch] = useReducer(discountCouponReducer, defaulDiscountCoupontState);

    const pPole = useMemo(() => pageSettings.field_proste_pola, [pageSettings]);
    const isAddedDiscountCoupon = useMemo(() => !!(configurator.basket.discountCoupon && configurator.basket.discountCoupon.code), [configurator]);

    // on mount, update
    useEffect(() => {
        // Remove funding from basket if is disabled
        allFundings && allFundings.forEach(funding => {
            if (funding.disabled) {
                configuratorActions.basketRemoveFunding(funding);
            }
        });

        // Remove funding from basket if not exist
        basket.fundings.forEach(basketFunding => {
            if(allFundings && !allFundings.some(funding => funding.id === basketFunding.id)) {
                configuratorActions.basketRemoveFunding(basketFunding);
            }
        });
    }, [allFundings, basket.fundings.length]);

    useEffect(() => {
        if(paymentType === 'lease' && isAddedDiscountCoupon) {
            removeDiscountCoupon();
            discountCouponDispatch({ type: "set_is_error", payload: false });
            onAfterRemoveDiscount && onAfterRemoveDiscount();
        }
    }, [paymentType, isAddedDiscountCoupon])

    useEffect(() => {
        ReactTooltip.rebuild();
    }, [currentFundings])

    const addDiscountCoupon = () => {
        discountCouponDispatch({ type: "set_is_loading", payload: true });
        fetch(`${process.env.IPOS_IPSALE_API_URL}/coupon/${discountCoupon.code}`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(response => {
                if (response) {
                    configuratorActions.setDiscountCoupon({
                        code: discountCoupon.code,
                        discount:
                            (get(response, 'parameters.discount', 0) ||
                                get(response, 'typeParameters.discount', 0)) / 100,
                        desc: response.desc,
                    } as BasketDiscountCoupon);
                    discountCouponDispatch({ type: "set_is_error", payload: false });
                } else {
                    discountCouponDispatch({ type: "set_is_error", payload: true });
                }
            })
            .catch(() => {
                discountCouponDispatch({ type: "set_is_error", payload: true });
            })
            .finally(() => {
                discountCouponDispatch({ type: "set_is_loading", payload: false });
            });
    }

    const removeDiscountCoupon = () => {
        configuratorActions.removeDiscountCoupon(true);
        discountCouponDispatch({ type: "set_code", payload: "" });
    }

    const handleDiscountCouponClick = () => {
        isAddedDiscountCoupon
            ? removeDiscountCoupon()
            : addDiscountCoupon();
    }

    const handleDiscountCouponChange = (e: React.FormEvent<HTMLInputElement>) => {
        discountCouponDispatch({ type: "set_code", payload: e.currentTarget.value });
    }

    const handleFundingClick = (funding: Funding, isAdded: boolean) => () => {
        if (funding.disabled) return;
        isAdded
            ? configuratorActions.basketRemoveFunding(funding)
            : configuratorActions.basketAddFunding(funding);
    }

    const handleOpenAccordion = () => ReactTooltip.rebuild();

    return (
        <>
            <Accordion trigger={pPole ? pPole[130] : ''} triggerWhenOpen={pPole ? pPole[142] : ''} onOpen={handleOpenAccordion}>
                <StyledFundings>
                    {allFundings && allFundings
                        .filter(funding => !funding.disabled)
                        .map(funding => {
                            const isFundingAdded = !!currentFundings.find(f => f.id === funding.id);
                            return (
                                <StyledFunding
                                    key={funding.id}
                                >
                                    <StyledWrapper>
                                        <StyledName isAdded={isFundingAdded}>
                                            {isFundingAdded ? funding.sidePanelInfo : funding.name}
                                        </StyledName>
                                        {
                                            (!isFundingAdded && funding.description) && (
                                                <TooltipIcon
                                                    data-html={true}
                                                    data-tip={funding.description}
                                                    data-for="tooltipFounding"
                                                    data-place={window.innerWidth >= 1024 ? "top" : "bottom" }
                                                />
                                            )
                                        }
                                    </StyledWrapper>
                                    <StyledButton
                                        isAdded={isFundingAdded}
                                        onClick={handleFundingClick(funding, isFundingAdded)}
                                    >
                                        {isFundingAdded 
                                        ? (pPole ? pPole[132] : '') 
                                        : (pPole ? pPole[131] : '')
                                    }
                                    </StyledButton>
                                </StyledFunding>
                            )
                        })}
                    </StyledFundings>
                <StyledDiscountCoupon>
                    <StyledWrapper>
                        <StyledName isAdded={isAddedDiscountCoupon}>
                            {
                                (isAddedDiscountCoupon && configurator.basket.discountCoupon)
                                    ? `${pPole ? pPole[133] : ''} ${configurator.basket.discountCoupon.code}.
                                    ${configurator.basket.discountCoupon.desc} (-${utils.formatCurrency(configurator.basket.discountCoupon.discount)}).`
                                    : pPole ? pPole[39] : ''
                            }
                        </StyledName>
                       <StyledDiscountCouponWrapper>
                            {
                                !isAddedDiscountCoupon && (
                                    <StyledInput
                                        type="text"
                                        placeholder={pPole ? pPole[40] : ''}
                                        disabled={isAddedDiscountCoupon || paymentType === 'lease'}
                                        onChange={handleDiscountCouponChange}
                                        value={discountCoupon.code}
                                        isError={discountCoupon.isError}
                                    />
                                )
                            }
                            <StyledButton
                                isAdded={isAddedDiscountCoupon}
                                disabled={discountCoupon.isLoading || paymentType === 'lease'}
                                onClick={handleDiscountCouponClick}
                            >
                                {isAddedDiscountCoupon 
                                ? (pPole ? pPole[134] : '')
                                : (pPole ? pPole[135] : '')}
                            </StyledButton>
                       </StyledDiscountCouponWrapper>
                    </StyledWrapper>
                    {
                        discountCoupon.isError && (
                            <StyledDiscountCouponError>
                                {pPole ? pPole[136] : ''}
                            </StyledDiscountCouponError>
                        )
                    }
                </StyledDiscountCoupon>
                <StyledReturnPayment>
                    <StyledWrapper>
                        <StyledName>
                            {pPole ? pPole[92] : ''}
                        </StyledName>
                    </StyledWrapper>
                    {
                        (pageSettings.field_pola_wysywig && pageSettings.field_pola_wysywig[1].value) && (
                            <TooltipIcon
                                data-html={true}
                                data-tip={pageSettings.field_pola_wysywig[1].value}
                                data-for="tooltipReturnPayment"
                            />
                        )
                    }
                </StyledReturnPayment>
            </Accordion>
            <BorderLine color={colors.summaryBorder} />
        </>
    )
}

export default ShoppingSummaryDiscounts;
